<template>
    <section>
        <waitingBlock v-if="isGetLanguages || isGetCurrentDayTemplate"></waitingBlock>
        <div v-else>
            <div class="highlights">
                <div class="highlight" v-for="highlight in highlights">
                    <h1>{{highlight.label}}</h1>

                    <div class="count">{{highlight.count}}</div>
                </div>
            </div>
            <div class="graphs">
                <SolidGaugeChart
                        :initial-data="stats.byType"
                        :fields="{value: 'ratio', category: 'name', max: 'maxPercentage'}"
                        :numeric-fields="['ratio', 'minutePlayDuration']">
                    <template v-slot:title>Total time by file type</template>
                    <template v-slot:label="{ colors, index, record, othersData }">
                        <span class="d-flex align-items-center"><span class="label-color mr-2"
                                                                      :style="`background-color: ${colors[index]};`"></span> {{ record['name'] }}</span>
                        <div class="label-value">
                            <span class="font-weight-bold">{{ minutesToDuration(record.minutePlayDuration) }} ·</span>
                            <span class="d-inline-block text-right" style="width: 40px;">{{ Math.round(record.ratio * 100) / 100 }}%</span>
                        </div>
                    </template>
                </SolidGaugeChart>
                <Lines
                        :initial-data="stats.byStyle"
                        :fields="{value: 'ratio', category: 'name', max: 'maxPercentage'}"
                        :numeric-fields="['count', 'ratio']">
                    <template v-slot:title>Genres</template>
                    <template v-slot:label:value="{ colors, index, record, othersData }">
                        <div class="label-value">
                            <span class="font-weight-bold">{{ record.count }}</span>
                        </div>
                    </template>
                </Lines>
                <SemiCirclePieChart
                        :initial-data="stats.byGender"
                        :fields="{value: 'count', category: 'name', ratio: 'ratio'}">
                    <template v-slot:title>Gender</template>
                    <template v-slot:label="{ colors, index, record, othersData }">
                        <span class="d-flex align-items-center"><span class="label-color mr-2"
                                                                      :style="`background-color: ${colors[index]};`"></span> {{ record['name'] }}</span>
                        <div class="label-value">
                            <span class="font-weight-bold">{{ record.count }} ·</span>
                            <span class="d-inline-block text-right" style="width: 40px;">{{ Math.round(record.ratio * 100) / 100 }}%</span>
                        </div>
                    </template>
                </SemiCirclePieChart>
                <RadarChart
                        :initial-data="stats.byLanguage"
                        :fields="{value: 'count', category: 'name', ratio: 'ratio'}">
                    <template v-slot:title>Languages</template>
                </RadarChart>
                <SemiCirclePieChart
                        :initial-data="stats.byMood"
                        :fields="{value: 'count', category: 'name', ratio: 'ratio'}"
                        :tooltip="(data) => { return `<i class='${Constants.moods[data.category].icon}' /> <span class='font-weight-bold'>${data.value}</span>`; }"
                        type="mood">
                    <template v-slot:title>Moods</template>
                    <template v-slot:label="{ colors, index, record, othersData }">
                      <span class="d-flex align-items-center">
                        <span class="label-color mr-2" :style="`background-color: ${colors[index]};`"></span>
                        <i :class="Constants.moods[record['name']].icon"/>
                      </span>
                      <div class="label-value">
                          <span class="font-weight-bold">{{ record.count }} ·</span>
                          <span class="d-inline-block text-right" style="width: 40px;">{{ Math.round(record.ratio * 100) / 100 }}%</span>
                      </div>
                    </template>
                </SemiCirclePieChart>
                <Lines
                        :initial-data="stats.byYear"
                        :fields="{value: 'ratio', category: 'name', max: 'maxPercentage'}"
                        :numeric-fields="['count', 'ratio']"
                        type="rangeFrom">
                    <template v-slot:title>Years</template>
                    <template v-slot:label:value="{ colors, index, record, othersData }">
                        <div class="label-value">
                            <span class="font-weight-bold">{{ record.count }}</span>
                        </div>
                    </template>
                </Lines>
                <SemiCirclePieChart
                        :initial-data="stats.byEnergy"
                        :fields="{value: 'count', category: 'energy', ratio: 'ratio'}"
                        :tooltip="(data) => { return `<i class='${Constants.energies[data.category].icon}' /> <span class='font-weight-bold'>${data.value}</span>`; }"
                        :is-sort-desc="false"
                        type="energy">
                    <template v-slot:title>Energy</template>
                    <template v-slot:label="{ colors, index, record, othersData }">
                            <span class="d-flex align-items-center">
                              <span class="label-color mr-2" :style="`background-color: ${colors[index]};`"></span>
                              <i :class="Constants.energies[record.energy].icon"/>
                            </span>
                        <div class="label-value">
                            <span class="font-weight-bold">{{ record.count }} ·</span>
                            <span class="d-inline-block text-right" style="width: 40px;">{{ Math.round(record.ratio * 100) / 100 }}%</span>
                        </div>
                    </template>
                </SemiCirclePieChart>
                <Lines
                        :initial-data="stats.byBpm"
                        :fields="{value: 'ratio', category: 'name', max: 'maxPercentage'}"
                        :numeric-fields="['count', 'ratio']"
                        type="rangeFrom">
                    <template v-slot:title>BPM</template>
                    <template v-slot:label:value="{ colors, index, record, othersData }">
                        <div class="label-value">
                            <span class="font-weight-bold">{{ record.count }}</span>
                        </div>
                    </template>
                </Lines>
            </div>
        </div>
    </section>
</template>
<script>
    import moment from "moment";
    import {minutesToDuration} from "@/lib/formatting";
    import Constants from '@/store/constants';
    import Translations from '@/store/translations';

    import WaitingBlock from "@/components/common/WaitingBlock";
    import Lines from "@/components/common/stats/Lines";
    import DonutChart from "@/components/common/stats/DonutChart";
    import PieChart from "@/components/common/stats/PieChart";
    import RadarChart from "@/components/common/stats/RadarChart";
    import SemiCirclePieChart from "@/components/common/stats/SemiCirclePieChart";
    import SolidGaugeChart from "@/components/common/stats/SolidGaugeChart";
    import {mapState} from "vuex";
    import {safeObj} from "@/utils/safeObj";

    export default {
        name: "PlanningStats",
        components: {WaitingBlock, Lines, DonutChart, PieChart, RadarChart, SemiCirclePieChart, SolidGaugeChart},
        computed: {
            ...mapState({
                languages: state => state.Templates.languages,
                currentDayTemplate: state => state.Templates.currentDayTemplate,
                isGetCurrentDayTemplate: state => state.Templates.isGetCurrentDayTemplate,
                isGetLanguages: state => state.Templates.isGetLanguages,
            }),
            minutesToDuration: () => minutesToDuration,
            scheduledDate() {
                return moment(this.$route.params.planningDate);
            },
            dayTemplateUid() {
                return safeObj(this.currentDayTemplate) && this.currentDayTemplate.dayTemplate.uid
            },
            stats() {
                const stats = safeObj(this.currentDayTemplate) && this.currentDayTemplate.statistics.trackStats;
                Object.keys(stats).map(key => {
                    if (stats[key].length === 0) {
                        return false
                    }

                    if (key === 'byLanguage') {
                        stats[key].map(record => {
                            const language = this.languages.find(language => language.code === record.code)

                            record.name = language ? language.name : record.code

                            return record
                        })
                    }

                    if (key === 'byGender') {
                        stats[key].map(record => {
                            record.name = Translations.genders[record.name] || record.name

                            return record
                        })
                    }

                    if (key === 'byType') {
                        stats[key].map(record => {
                            record.name = Translations.trackTypes[record.name] || record.name

                            return record
                        })
                        // Hide Voice Tracks & Undefined types
                        stats[key] = stats[key].filter(record => {
                            let recordType = Object.values(Constants.trackTypes).find(type => type.value === record.typeId)

                            return !['UNDEFINED', 'VOICE_TRACK', 'PROMO', 'FILLER'].includes(recordType.key)
                        })
                    }

                    if (key === 'byMood') {
                        stats[key].map(record => {
                            record.name = Translations.moods[record.mood] || record.mood
                            record.tooltip = `<i class="${Constants.moods[record.mood].icon}" />`

                            return record
                        })
                    }

                    if (key === 'byEnergy') {
                        stats[key].map(record => {
                            record.name = Object.values(Translations.energies)[record.energy] || record.energy

                            return record
                        })
                    }

                    if (key === 'byYear') {
                        stats[key].map(record => {
                            record.name = record.rangeFrom + '\'s'

                            return record
                        })
                    }

                    if (key === 'byBpm') {
                        stats[key].map(record => {
                            record.name = record.rangeFrom + ' - ' + record.rangeTo

                            return record
                        })
                    }

                    stats[key].map(record => {
                        record.maxPercentage = 100

                        return record
                    })
                })

                return stats
            },
            highlights() {
                const statistics = safeObj(this.currentDayTemplate) && this.currentDayTemplate.statistics;

                return {
                    musics: {
                        label: 'Music Tracks',
                        count: statistics.trackCount || 0
                    },
                    podcasts: {
                        label: 'Podcasts',
                        count: statistics.podcastCount || 0
                    },
                    adverts: {
                        label: 'Adverts',
                        count: statistics.advertCount || 0
                    }
                }
            },
        },
        async beforeMount() {
            await this.$store.dispatch('Templates/getLanguages');

            await this.$store.dispatch('Templates/getCurrentDayTemplate', {
                scheduleDate: this.scheduledDate.format("YYYY-MM-DD"),
                stationUid: this.$route.params.stationUid
            });

        },
        data() {
            return {
                Constants
            }
        }
    };
</script>
<style lang="scss">
    .graph-container {
        width: calc(50% - 5px);
        margin-top: 20px;
        background: white;
        border-radius: 4px;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
        display: flex;
        flex-direction: column;

        header {
            color: #292c33;
            border-bottom: 1px solid var(--COLOR_BORDERS);
            padding: 10px;
            font-size: 16px;
            font-weight: 600;
            letter-spacing: 0.2px;
            line-height: 20px;
        }

        .graph {
            min-height: 275px;
        }
    }

    @media (max-width: 1200px) {
        .graph-container {
            width: 100%;
        }
    }
</style>
<style scoped>
    .highlights {
        display: flex;
        color: #292c33;
        background-color: #e1e0e1;
        border-radius: 3px;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
    }

    .highlights .highlight {
        width: calc(100% / 3);
        padding: 20px;
        background: white;
        margin: 1px;
    }

    @media (max-width: 1200px) {
        .highlights {
            flex-wrap: wrap;
        }

        .highlights .highlight {
            width: calc(50% - 2px);
        }
    }

    .highlight h1 {
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0.2px;
        line-height: 20px;
    }

    .highlight div.count {
        margin-top: 15px;
        font-size: 40px;
        font-weight: bold;
        line-height: 49px;
    }

    .graphs {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        padding-top: 10px;
    }
</style>
